import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppAdminFormLabel,
  AppBreadcrumb,
  AppButton,
  AppCard,
  AppContainer,
  AppSelect,
  AppTextField,
} from '@/components/atoms';
import { useAdminLatlonCacheCreate } from '@/hooks/admin/useAdminLatlonCaches';
import { useClinicOptionsQuery } from '@/hooks/admin/useClinicOptionsQuery';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import { LATITUDE_REGEX, LONGITUDE_REGEX } from '@/utils/geoCoordinate';

const SiteAdminLatlonCachesCreatePage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { currentSite } = useSiteAdminAuth();
  const siteType = site ?? currentSite ?? 'super-admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const listPath = `/${siteType}/admin/latlon_caches`;

  const { data: clinicOptions, isLoading: isClinicsLoading } = useClinicOptionsQuery(siteType);

  const clinicSelectOptions = useMemo(
    () =>
      (clinicOptions ?? []).map(c => ({
        label: c.clinic_number,
        value: c.id,
        searchKey: c.clinic_number,
      })),
    [clinicOptions]
  );

  const [clinicId, setClinicId] = useState<number | null>(null);
  const [address, setAddress] = useState('');
  const [addressGeop, setAddressGeop] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const hasSetInitialClinicRef = useRef(false);
  useEffect(() => {
    if (!hasSetInitialClinicRef.current && clinicSelectOptions.length > 0) {
      setClinicId(clinicSelectOptions[0].value);
      hasSetInitialClinicRef.current = true;
    }
  }, [clinicSelectOptions]);

  const { createLatlonCacheMutation, isLoading: isCreating } = useAdminLatlonCacheCreate();

  const breadcrumbItems = [
    { title: '緯度経度キャッシュ', href: listPath },
    { title: '新規' },
  ];

  const validateForm = (): boolean => {
    const errors: Record<string, string[]> = {};

    if (!clinicId) {
      errors.clinic_id = ['治療院IDは必須です。'];
    }

    if (!address.trim()) {
      errors.address = ['住所は必須です。'];
    } else if (address.length > 100) {
      errors.address = ['住所は100文字以下にしてください。'];
    }

    if (addressGeop.length > 100) {
      errors.address_geop = ['geop住所は100文字以下にしてください。'];
    }

    if (!latitude.trim()) {
      errors.latitude = ['緯度は必須です。'];
    } else if (isNaN(Number(latitude))) {
      errors.latitude = ['緯度は数字にしてください。'];
    } else if (!LATITUDE_REGEX.test(latitude.trim())) {
      errors.latitude = ['緯度の書式が正しくありません。'];
    }

    if (!longitude.trim()) {
      errors.longitude = ['経度は必須です。'];
    } else if (isNaN(Number(longitude))) {
      errors.longitude = ['経度は数字にしてください。'];
    } else if (!LONGITUDE_REGEX.test(longitude.trim())) {
      errors.longitude = ['経度の書式が正しくありません。'];
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }
    if (!clinicId) return;

    try {
      const response = await createLatlonCacheMutation({
        site: siteType,
        payload: {
          clinic_id: clinicId,
          address,
          address_geop: addressGeop || null,
          latitude,
          longitude,
        },
      });
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'latlon-caches'] });
        navigate(listPath, { replace: true });
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { errors?: Record<string, string[]> } };
      };
      if (axiosErr.response?.status === 422 && axiosErr.response.data?.errors) {
        setFieldErrors(axiosErr.response.data.errors);
      } else {
        toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
      }
    }
  };

  const clearFieldError = (name: string) => {
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const renderField = (
    label: string,
    name: string,
    value: string,
    onChange: (v: string) => void,
    required = false
  ) => (
    <div className="mb-4">
      <AppAdminFormLabel label={label} required={required} />
      <AppTextField
        value={value}
        onChange={e => {
          onChange(e.target.value);
          clearFieldError(name);
        }}
        status={fieldErrors[name] ? 'error' : undefined}
        className="w-full"
      />
      {fieldErrors[name]?.map((msg, i) => (
        <p key={i} className="mt-1 text-sm text-red-600">
          {msg}
        </p>
      ))}
    </div>
  );

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} />

      <AppCard title="緯度経度キャッシュ新規">
        <div className="mt-2">
          <div className="mb-4">
            <AppAdminFormLabel label="治療院ID" required />
            <AppSelect
              className="w-full"
              placeholder=""
              showSearch
              allowClear
              optionFilterProp="searchKey"
              loading={isClinicsLoading}
              value={clinicId ?? undefined}
              status={fieldErrors.clinic_id ? 'error' : undefined}
              options={clinicSelectOptions}
              onChange={value => {
                setClinicId(typeof value === 'number' ? value : null);
                clearFieldError('clinic_id');
              }}
            />
            {fieldErrors.clinic_id?.map((msg, i) => (
              <p key={i} className="mt-1 text-sm text-red-600">
                {msg}
              </p>
            ))}
          </div>

          {renderField('住所', 'address', address, setAddress, true)}
          {renderField('GeOAP住所', 'address_geop', addressGeop, setAddressGeop)}
          {renderField('緯度', 'latitude', latitude, setLatitude, true)}
          {renderField('経度', 'longitude', longitude, setLongitude, true)}

          <div className="flex gap-3 mt-6">
            <AppButton
              type="primary"
              loading={isCreating}
              onClick={handleCreate}
            >
              更新
            </AppButton>
            <AppButton
              onClick={() => navigate(listPath)}
              disabled={isCreating}
            >
              前画面に戻る
            </AppButton>
          </div>
        </div>
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminLatlonCachesCreatePage;
